import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from '../Sidebar/nav';

interface HeaderProps {
	onBurger?: () => void;
}

interface CmdItem {
	icon: string;
	label: string;
	tag: string;
	run: () => void;
}

const isProd = (process.env.REACT_APP_API_BASE_URL || '').includes('api.uomox.com');

const Header = ({ onBurger = () => { } }: HeaderProps) => {
	const navigate = useNavigate();
	const [cmdOpen, setCmdOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [sel, setSel] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	// Ctrl/Cmd + K opens the command palette, Esc closes it
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
				e.preventDefault();
				setCmdOpen(true);
			} else if (e.key === 'Escape') {
				setCmdOpen(false);
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	useEffect(() => {
		if (cmdOpen) {
			setQuery('');
			setSel(0);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [cmdOpen]);

	const q = query.trim().toLowerCase();
	const items: CmdItem[] = [
		...(q ? [{
			icon: 'ri-search-line',
			label: `Search customers for “${query.trim()}”`,
			tag: 'Customers',
			run: () => navigate(`/customers-and-users?search=${encodeURIComponent(query.trim())}`),
		}] : []),
		...NAV_ITEMS.filter(n => !q || n.label.toLowerCase().includes(q)).map(n => ({
			icon: n.icon, label: n.label, tag: 'Page', run: () => navigate(n.path),
		})),
	];

	const run = (i: number) => {
		const it = items[i];
		setCmdOpen(false);
		if (it) it.run();
	};

	const onCmdKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(items.length - 1, s + 1)); }
		else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(0, s - 1)); }
		else if (e.key === 'Enter') { e.preventDefault(); run(sel); }
	};

	return (
		<>
			<header className="ma-top">
				<button type="button" className="ma-burger" onClick={onBurger} aria-label="Open menu"><i className="ri-menu-2-line"></i></button>
				<div className="ma-search" onClick={() => setCmdOpen(true)}>
					<i className="ri-search-line"></i>
					<span>Search customers or jump to a page…</span>
					<span className="ma-kbd">Ctrl K</span>
				</div>
				<div className="sp"></div>
				<span className={`ma-env ${isProd ? '' : 'dev'}`}><i></i>{isProd ? 'PRODUCTION' : 'LOCAL'}</span>
				<div className="ma-me">
					<div className="ma-av">A</div>
					<div><b>Admin</b>Master Admin</div>
				</div>
			</header>

			{cmdOpen && (
				<>
					<div className="ma-scrim" onClick={() => setCmdOpen(false)}></div>
					<div className="ma-cmd">
						<input
							ref={inputRef}
							placeholder="Type a customer name or a page…"
							value={query}
							onChange={(e) => { setQuery(e.target.value); setSel(0); }}
							onKeyDown={onCmdKey}
						/>
						<div className="res">
							{items.length ? items.map((it, i) => (
								<div key={it.label} className={`it ${i === sel ? 'sel' : ''}`} onMouseEnter={() => setSel(i)} onClick={() => run(i)}>
									<i className={it.icon}></i>{it.label}<small>{it.tag}</small>
								</div>
							)) : <div className="none">No matches</div>}
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default Header;
